<script setup>
import { onMounted, ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLeadSourcesStore } from 'dashboard/stores/leadSources';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const store = useLeadSourcesStore();
const router = useRouter();
const { t } = useI18n();

const newDialogRef = ref(null);
const deleteDialogRef = ref(null);
const selectedSource = ref(null);
const selectedKind = ref('site_form');
const customName = ref('');
const creating = ref(false);

// Os kinds internos (manual/ai_conversation/csv_import) são auto-seedados pelo
// backend e não devem ser criados manualmente; só expomos os públicos no dialog.
const kindOptions = [
  {
    key: 'site_form',
    label: 'LEAD_SOURCES.KIND.SITE_FORM',
    icon: 'i-lucide-globe',
    description:
      'Form embedado no site/landing - POST direto pro endpoint público.',
  },
  {
    key: 'webhook_api',
    label: 'LEAD_SOURCES.KIND.WEBHOOK_API',
    icon: 'i-lucide-webhook',
    description: 'Sistema externo dispara POST quando há novo lead.',
  },
  {
    key: 'poller',
    label: 'LEAD_SOURCES.KIND.POLLER',
    icon: 'i-lucide-refresh-cw',
    description: 'Cron puxa de API externa periodicamente.',
  },
];

// Lookup de metadata (label/icon) por kind, inclusive os internos.
const kindMeta = {
  site_form: {
    label: 'LEAD_SOURCES.KIND.SITE_FORM',
    icon: 'i-lucide-globe',
    color: '#0ea5e9',
  },
  webhook_api: {
    label: 'LEAD_SOURCES.KIND.WEBHOOK_API',
    icon: 'i-lucide-webhook',
    color: '#8b5cf6',
  },
  poller: {
    label: 'LEAD_SOURCES.KIND.POLLER',
    icon: 'i-lucide-refresh-cw',
    color: '#f59e0b',
  },
  csv_import: {
    label: 'LEAD_SOURCES.KIND.CSV_IMPORT',
    icon: 'i-lucide-file-spreadsheet',
    color: '#10b981',
  },
  manual: {
    label: 'LEAD_SOURCES.KIND.MANUAL',
    icon: 'i-lucide-hand',
    color: '#64748b',
  },
  ai_conversation: {
    label: 'LEAD_SOURCES.KIND.AI_CONVERSATION',
    icon: 'i-lucide-bot',
    color: '#ec4899',
  },
};

const sortedList = computed(() =>
  [...store.list].sort((a, b) => {
    // Públicas primeiro, depois ativos, depois nome.
    const aPublic = ['site_form', 'webhook_api', 'poller'].includes(a.kind);
    const bPublic = ['site_form', 'webhook_api', 'poller'].includes(b.kind);
    if (aPublic !== bPublic) return aPublic ? -1 : 1;
    if (a.active !== b.active) return a.active ? -1 : 1;
    return a.name.localeCompare(b.name);
  })
);

onMounted(() => store.fetchAll());

function openNew() {
  newDialogRef.value?.open();
}
function closeNew() {
  newDialogRef.value?.close();
}

async function confirmCreate() {
  if (creating.value) return;
  creating.value = true;
  try {
    const meta = kindMeta[selectedKind.value];
    const name = customName.value.trim() || t(meta.label);
    const created = await store.create({
      name,
      kind: selectedKind.value,
      active: true,
    });
    closeNew();
    customName.value = '';
    selectedKind.value = 'site_form';
    useAlert('Fonte criada.');
    router.push({
      name: 'settings_lead_source_edit',
      params: { sourceId: created.id },
    });
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Falha ao criar fonte.');
  } finally {
    creating.value = false;
  }
}

function onClickEdit(source) {
  router.push({
    name: 'settings_lead_source_edit',
    params: { sourceId: source.id },
  });
}

function onClickDelete(source) {
  selectedSource.value = source;
  deleteDialogRef.value?.open();
}

async function confirmDelete() {
  const source = selectedSource.value;
  if (!source) return;
  try {
    await store.destroy(source.id);
    useAlert('Fonte excluída.');
  } catch (e) {
    useAlert(
      e?.response?.data?.error ||
        e?.response?.data?.errors?.join?.(', ') ||
        'Falha ao excluir.'
    );
  } finally {
    deleteDialogRef.value?.close();
    selectedSource.value = null;
  }
}

function runStatusBadge(source) {
  if (!source.last_run_at)
    return {
      label: t('LEAD_SOURCES.RUN_STATUS.NEVER'),
      cls: 'bg-n-alpha-2 text-n-slate-11',
    };
  if (source.last_run_status === 'error')
    return {
      label: t('LEAD_SOURCES.RUN_STATUS.ERROR'),
      cls: 'bg-n-ruby-3 text-n-ruby-11',
    };
  return {
    label: t('LEAD_SOURCES.RUN_STATUS.OK'),
    cls: 'bg-n-grass-3 text-n-grass-11',
  };
}
</script>

<template>
  <SettingsLayout
    :is-loading="store.loading"
    loading-message="Carregando fontes..."
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('LEAD_SOURCES.HEADER')"
        :description="t('LEAD_SOURCES.DESCRIPTION')"
        feature-name="lead-sources"
      >
        <template v-if="store.list.length" #count>
          <span class="text-body-main text-n-slate-11"
            >{{ store.list.length }} fonte(s)</span
          >
        </template>
        <template #actions>
          <NextButton
            icon="i-lucide-plus"
            size="sm"
            :label="t('LEAD_SOURCES.NEW')"
            @click="openNew"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template v-if="!store.list.length" #body>
      <!-- Empty state inline - sem componente dedicado pra evitar criar novo
           shell visual; segue mesma estrutura usada por outras telas de
           settings sem CrmEmptyState dedicado. -->
      <div
        class="flex flex-col items-center justify-center text-center py-20 px-6 gap-4"
      >
        <span
          class="size-14 rounded-full bg-n-alpha-2 flex items-center justify-center"
        >
          <i class="i-lucide-inbox size-6 text-n-slate-11" />
        </span>
        <div>
          <h2 class="text-heading-3 text-n-slate-12 mb-1">
            {{ t('LEAD_SOURCES.EMPTY') }}
          </h2>
          <p class="text-body-main text-n-slate-11 max-w-md">
            {{ t('LEAD_SOURCES.EMPTY_DESCRIPTION') }}
          </p>
        </div>
        <NextButton
          icon="i-lucide-plus"
          :label="t('LEAD_SOURCES.NEW')"
          @click="openNew"
        />
      </div>
    </template>

    <template v-else #body>
      <div class="flex flex-col gap-2 pt-2 pb-10">
        <ol class="flex flex-col gap-2">
          <li
            v-for="source in sortedList"
            :key="source.id"
            class="rounded-xl border border-n-weak hover:border-n-slate-7 transition-colors px-4 py-3 flex items-center gap-4"
          >
            <span
              class="size-10 rounded-full flex items-center justify-center flex-shrink-0"
              :style="{
                background: `${kindMeta[source.kind]?.color || '#64748b'}1f`,
              }"
            >
              <i
                :class="kindMeta[source.kind]?.icon || 'i-lucide-circle'"
                class="size-5"
                :style="{ color: kindMeta[source.kind]?.color || '#64748b' }"
              />
            </span>

            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 min-w-0 flex-wrap">
                <p class="text-heading-3 text-n-slate-12 truncate mb-0">
                  {{ source.name }}
                </p>
                <span
                  class="inline-flex items-center px-1.5 py-0.5 rounded bg-n-brand/10 text-label-small font-medium text-n-brand uppercase tracking-wide"
                  >{{ t(kindMeta[source.kind]?.label || source.kind) }}</span
                >
                <span
                  v-if="!source.active"
                  class="inline-flex items-center px-1.5 py-0.5 rounded bg-n-alpha-2 text-label-small font-medium text-n-slate-11 uppercase tracking-wide"
                  >inativo</span
                >
                <span
                  v-if="source.kind === 'poller'"
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-label-small font-medium uppercase tracking-wide"
                  :class="runStatusBadge(source).cls"
                  >{{ runStatusBadge(source).label }}</span
                >
              </div>
              <div
                class="flex items-center gap-4 mt-1 text-body-small text-n-slate-10"
              >
                <span class="inline-flex items-center gap-1">
                  <i class="i-lucide-inbox size-3" />
                  {{ source.submissions_count || 0 }} submissions
                </span>
                <span class="inline-flex items-center gap-1 font-mono">{{
                  source.slug
                }}</span>
                <span
                  v-if="source.last_run_at"
                  class="inline-flex items-center gap-1"
                >
                  <i class="i-lucide-clock size-3" />
                  {{ new Date(source.last_run_at).toLocaleString() }}
                </span>
              </div>
            </div>

            <div class="flex items-center gap-2 flex-shrink-0">
              <NextButton
                faded
                slate
                size="sm"
                icon="i-lucide-pen-square"
                label="Editar"
                @click="onClickEdit(source)"
              />
              <NextButton
                faded
                ruby
                size="sm"
                icon="i-lucide-trash-2"
                v-tooltip="'Excluir fonte'"
                @click="onClickDelete(source)"
              />
            </div>
          </li>
        </ol>
      </div>
    </template>

    <Dialog
      ref="newDialogRef"
      width="2xl"
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <div class="flex flex-col gap-4">
        <div>
          <h3 class="text-heading-3 text-n-slate-12 mb-1">
            Nova fonte de leads
          </h3>
          <p class="text-body-small text-n-slate-11">
            Escolha o tipo de captura. Fontes internas (manual, IA, CSV) já vêm
            pré-configuradas pela conta.
          </p>
        </div>

        <div class="grid grid-cols-1 gap-2">
          <button
            v-for="opt in kindOptions"
            :key="opt.key"
            type="button"
            class="text-left p-3 rounded-xl border-2 transition flex items-start gap-3"
            :class="
              selectedKind === opt.key
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:border-n-slate-7'
            "
            @click="selectedKind = opt.key"
          >
            <div
              class="size-9 rounded-full bg-n-alpha-2 flex items-center justify-center flex-shrink-0"
            >
              <i :class="opt.icon" class="size-4 text-n-slate-12" />
            </div>
            <div class="min-w-0">
              <div class="text-body-main font-semibold text-n-slate-12">
                {{ t(opt.label) }}
              </div>
              <p class="text-label-small text-n-slate-11 mt-0.5 mb-0">
                {{ opt.description }}
              </p>
            </div>
          </button>
        </div>

        <Input
          id="lead-source-name"
          v-model="customName"
          label="Nome (opcional)"
          placeholder="Ex: Form do site principal"
        />

        <div class="flex justify-end gap-2 pt-2">
          <NextButton faded slate label="Cancelar" @click="closeNew" />
          <NextButton
            :is-loading="creating"
            label="Criar"
            @click="confirmCreate"
          />
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      title="Excluir fonte"
      :description="`Excluir a fonte “${selectedSource?.name}”? Submissions e leads associados ficarão sem origem registrada.`"
      confirm-button-label="Excluir"
      cancel-button-label="Cancelar"
      @confirm="confirmDelete"
    />
  </SettingsLayout>
</template>
